// Търсачката, която добавя продукт към правилото, + „Целият списък" до нея.
// Стои в „следващото празно място" на веригата (Продукт 1 / Продукт 2 / Още
// продукт). Текстът ѝ живее в СТРАНИЦАТА (както при доставка/изписване), за да
// може писането където и да е на екрана да влиза направо в нея.
//
// Клавиатура: ↑/↓ в резултатите, Enter взема маркирания (Ctrl/Cmd+Enter — не:
// той е записът), Esc чисти текста; при затворен списък ↑ отива на
// количеството на последния продукт (веригата е НАД търсачката).
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Input } from "antd";
import { AppstoreOutlined, SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { Product } from "../../../../api/inventory";
import { highlightWords } from "../../components/highlight";
import { SECONDARY_BUTTON_CLASS } from "../../components/list/buttonStyles";
import { useInventoryShell } from "../../components/shell/InventoryShellContext";

// Колко място иска падащият списък под полето, преди да се обърне нагоре
const DROPDOWN_ROOM_PX = 260;

type Props = {
  // продуктите, които още не са в групата
  availableProducts: Product[];
  search: string;
  onSearchChange: (value: string) => void;
  attachInputRef: (node: HTMLInputElement | null) => void;
  onAdd: (product: Product) => void;
  onOpenCatalog: () => void;
  // ↑ при затворен списък (false — няма къде, клавишът не се консумира)
  onArrowUpOut: () => boolean;
};

export const InterchangeRuleSearch = ({
  availableProducts,
  search,
  onSearchChange,
  attachInputRef,
  onAdd,
  onOpenCatalog,
  onArrowUpOut,
}: Props) => {
  const { t } = useTranslation();
  const { isNarrow } = useInventoryShell();
  const [searchFocused, setSearchFocused] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [opensUpward, setOpensUpward] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Търсенето е МНОГОДУМНО, както при доставка/изписване: всяка дума трябва да
  // се среща някъде в името, затова „с я" намира „Сок ябълка".
  const searchWords = useMemo(
    () => search.trim().toLowerCase().split(/\s+/).filter(Boolean),
    [search],
  );
  const results = useMemo(() => {
    if (searchWords.length === 0) return [];
    return availableProducts
      .filter((product) => {
        const name = product.name.toLowerCase();
        return searchWords.every((word) => name.includes(word));
      })
      .sort((a, b) => a.name.localeCompare(b.name, "bg"))
      .slice(0, 40);
  }, [availableProducts, searchWords]);

  // Списъкът излиза САМО при писане — иначе след добавяне на продукт (фокусът
  // се връща в търсачката) щеше да покрива веригата.
  const dropdownOpen = searchFocused && results.length > 0;

  useEffect(() => {
    setHighlightedIndex(0);
  }, [search]);

  // Търсачката е в КРАЯ на веригата — близо до долния ръб на скролиращата
  // област списъкът се отваря НАГОРЕ, иначе се реже.
  useLayoutEffect(() => {
    if (!dropdownOpen || !wrapperRef.current) return;
    const field = wrapperRef.current.getBoundingClientRect();
    const area = wrapperRef.current.closest("[data-ich-scroll]")?.getBoundingClientRect();
    if (!area) return;
    const roomBelow = area.bottom - field.bottom;
    setOpensUpward(roomBelow < DROPDOWN_ROOM_PX && field.top - area.top > roomBelow);
  }, [dropdownOpen]);

  const pick = (product: Product) => {
    onAdd(product);
    onSearchChange("");
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent) => {
    if (!dropdownOpen) {
      if (event.key === "ArrowUp" && onArrowUpOut()) event.preventDefault();
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((index) => Math.min(index + 1, results.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === "Enter") {
      // Ctrl/Cmd+Enter не взема резултат — той е записът на правилото
      if (event.ctrlKey || event.metaKey) return;
      event.preventDefault();
      const product = results[highlightedIndex];
      if (product) pick(product);
    } else if (event.key === "Escape") {
      // Esc се консумира тук, за да не тръгне глобалната навигация „назад"
      event.preventDefault();
      onSearchChange("");
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div ref={wrapperRef} className="relative min-w-[200px] flex-1">
        <Input
          ref={(node) => attachInputRef(node?.input ?? null)}
          allowClear
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          onFocus={() => setSearchFocused(true)}
          // изчакваме клика в списъка (onMouseDown го предотвратява)
          onBlur={() => setSearchFocused(false)}
          onKeyDown={handleSearchKeyDown}
          prefix={<SearchOutlined className="mr-1 text-[17px] text-muted" />}
          placeholder={t("inventoryPage.interchangeAddProductPh")}
          // ! — antd cssinjs е извън Tailwind layer-ите и иначе бие класа;
          // 16px на телефон — иначе iOS зуумва при фокус
          className={`w-full !rounded-[10px] ${isNarrow ? "!text-base" : "!text-[15px]"}`}
        />
        {dropdownOpen && (
          <div
            className={`dk-elevated absolute inset-x-0 z-30 overflow-hidden rounded-xl border border-shade-3 bg-surface shadow-[0_18px_44px_rgba(20,15,8,0.16),0_4px_12px_rgba(20,15,8,0.08)] ${
              opensUpward ? "bottom-[calc(100%+6px)]" : "top-[calc(100%+6px)]"
            }`}
          >
            <div className="max-h-[min(42vh,360px)] overflow-y-auto">
              {results.map((product, index) => (
                <div
                  key={product.id}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => pick(product)}
                  onMouseMove={() => setHighlightedIndex(index)}
                  className={`flex cursor-pointer items-center gap-2.5 border-b border-shade-1 px-4 py-2.5 ${
                    index === highlightedIndex ? "bg-brand-tint" : "bg-surface"
                  }`}
                >
                  <span className="truncate text-[14px] font-semibold leading-[normal] text-ink">
                    {highlightWords(product.name, searchWords)}
                  </span>
                  {product.unit && (
                    <span className="shrink-0 text-xs leading-[normal] text-muted">{product.unit}</span>
                  )}
                  {product.category && (
                    <span className="ml-auto shrink-0 truncate text-xs leading-[normal] text-faint">
                      {product.category}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <button type="button" onClick={onOpenCatalog} className={`${SECONDARY_BUTTON_CLASS} !py-[9px]`}>
        <AppstoreOutlined />
        {t("inventoryPage.catalogOpen")}
      </button>
    </div>
  );
};

export default InterchangeRuleSearch;
